import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import type { Omit } from 'utility-types'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { FieldTypes } from 'modules/custom_resources/store/collections/customFields/fields'

import OptionRow from './OptionRow'
import type { Option } from './OptionRow'

const Choices = (props: { form: Form; disabled: boolean }) => {
  const { form, disabled } = props
  const formData = form.data()

  const [searchQuery, setSearchQuery] = React.useState('')
  const normalizedSearchQuery = searchQuery.trim().toLowerCase()

  const compareByLabel = (a: { label?: string }, b: { label?: string }) =>
    (a.label ?? '').localeCompare(b.label ?? '', undefined, {
      sensitivity: 'base',
      numeric: true,
    })

  const options = (formData.choice_options as Array<Omit<Option, 'index'>>).map(
    (option, index) => ({
      ...option,
      index,
    })
  )

  const draftOptions = options.filter((option) => option.draft)
  const createdOptions = options.filter((option) => !option.draft)
  const archivedOptions = createdOptions.filter((option) => !option.is_active)
  const activatedOptions = createdOptions.filter((option) => option.is_active)
  const createdOptionsOrderedForRender = [
    ...activatedOptions.slice().sort(compareByLabel),
    ...archivedOptions.slice().sort(compareByLabel),
  ]

  const shouldShowSearch = createdOptionsOrderedForRender.length >= 2

  const filteredCreatedOptionsOrderedForRender =
    normalizedSearchQuery.length === 0
      ? createdOptionsOrderedForRender
      : createdOptionsOrderedForRender.filter((option) =>
          (option.label ?? '').toLowerCase().includes(normalizedSearchQuery)
        )

  const isChoosable =
    formData.field_type === FieldTypes.singleChoice ||
    formData.field_type === FieldTypes.multipleChoice

  const onClickAdd = () => {
    const optionsForm = form.get('choice_options')
    optionsForm.set([
      ...[...activatedOptions, ...archivedOptions],
      ...draftOptions,
      {
        label: '',
        is_active: true,
        draft: true,
      },
    ])
  }

  const renderOptionRow = (option: Option) => {
    return (
      <Box key={option.index}>
        <OptionRow option={option} disabled={disabled} form={form} />
      </Box>
    )
  }

  if (!isChoosable) {
    return null
  }

  return (
    <FormRow>
      <Box gap="s16">
        <Text.Normal size="200" color="grey800">
          {i18n.t('settings.custom_fields.new.labels.single_choice_options.description')}
        </Text.Normal>

        {shouldShowSearch && (
          <TextField
            readonly={disabled}
            label={i18n.t('common.search')}
            placeholder={i18n.t('common.search_by')}
            value={searchQuery}
            onChange={setSearchQuery}
          />
        )}

        <Box gap="s8">
          {filteredCreatedOptionsOrderedForRender.map(renderOptionRow)}
          {draftOptions.map(renderOptionRow)}
        </Box>
        <Button type="secondary" onClick={onClickAdd} disabled={disabled} iconName="add">
          {i18n.t('settings.custom_fields.new.labels.single_choice_options.add')}
        </Button>
      </Box>
    </FormRow>
  )
}

export default observer(Choices)
